import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError, type InstanceGroupConsolidated, type InstanceGroupDetail } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { buildDialVM, type DialVM } from "../dialModel";
import { DialCard, latestUpdate } from "../components/DialCard";
import { Icon } from "../components/Icon";
import { timeAgo } from "../lib/ui";
import { ScoreBadge } from "../groups/groupUi";
import { AddMembersModal } from "../groups/AddMembersModal";
import { ConsolidatedMatrix } from "../groups/ConsolidatedMatrix";
import { GroupDial } from "../groups/GroupDial";
import { useHelp } from "../help/HelpController";
import { useRealtimeGroup } from "../realtime/useRealtime";
import { groups as hubGroups } from "../realtime/groups";

const token = () => getAccessToken() ?? "";

/**
 * An instance-first group view: a rolled-up overview of dials the user already owns. Each member is a
 * slice that drills into its own (unchanged) workspace; the dials stay viewable on their own.
 */
export function GroupWorkspace() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState<InstanceGroupDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [mode, setMode] = useState<"dial" | "portfolio" | "byArea">("portfolio");
  const [consolidated, setConsolidated] = useState<InstanceGroupConsolidated | null>(null);
  const [memberCards, setMemberCards] = useState<Map<string, MemberCardData>>(new Map());
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const refreshTimer = useRef<number | null>(null);

  // A group isn't a dial of either kind, so guided help offers the guides that apply to both (KEY-446).
  // The audience goes with it (KEY-535): a group has no palettes or subscribe bells of its own to judge, and
  // leaving a previous dial's answers standing here would also leave the first-run tour thinking a dial is
  // loaded and offer itself over a group.
  const { setDialKind, setHelpAudience } = useHelp();
  useEffect(() => {
    setDialKind(null);
    setHelpAudience(null);
  }, [setDialKind, setHelpAudience]);

  const load = useCallback(async () => {
    try {
      const g = await api.getGroup(token(), id);
      setGroup(g);
      // Fetch each member's detail so the portfolio shows the same rich dial cards as My Dials.
      // allSettled, deliberately: a group is NEVER blocked by a child (KEY-434). One child that has not
      // been told its emergency contact must not close a group covering forty sites — the group is instead
      // where you SEE that, so the child is badged and following it through lands on its capture screen.
      const details = await Promise.allSettled(g.members.map((m) => api.getInstance(token(), m.instanceId, m.organisationId)));
      const map = new Map<string, MemberCardData>();
      g.members.forEach((m, i) => {
        const r = details[i];
        if (r.status === "fulfilled") {
          const stamp = latestUpdate(r.value, "");
          map.set(m.instanceId, { vm: buildDialVM(r.value), updated: stamp ? timeAgo(stamp) : "" });
        } else if (r.reason instanceof ApiError && r.reason.status === 428) {
          map.set(m.instanceId, { vm: null, updated: "", needsSetup: true });
        }
      });
      setMemberCards(map);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 404
          ? "This group no longer exists."
          : e instanceof Error
            ? e.message
            : "Failed to load the group.",
      );
    }
  }, [id]);

  // The members are cross-org, so clear any per-dial scope before loading.
  useEffect(() => {
    api.setActiveOrg(null);
    void load();
  }, [load]);

  const loadConsolidated = useCallback(async () => {
    try {
      setConsolidated(await api.getGroupConsolidated(token(), id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load the by-area view.");
    }
  }, [id]);

  // Fetch the consolidated rollup on demand — both the dial and the by-area matrix need it.
  useEffect(() => {
    if (mode === "byArea" || mode === "dial") void loadConsolidated();
  }, [mode, loadConsolidated]);

  // Coalesce realtime member-status pushes into a single refresh of the detail (and the matrix, when shown).
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(() => {
      void load();
      if (modeRef.current === "byArea" || modeRef.current === "dial") void loadConsolidated();
    }, 600);
  }, [load, loadConsolidated]);

  async function rename() {
    const next = window.prompt("Rename group", group?.name)?.trim();
    if (!next || next === group?.name) return;
    setBusy(true);
    try {
      setGroup(await api.updateGroup(token(), id, { name: next }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not rename the group.");
    } finally {
      setBusy(false);
    }
  }

  async function changeStrategy(rollupStrategy: string) {
    setBusy(true);
    try {
      setGroup(await api.updateGroup(token(), id, { rollupStrategy }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not change the rollup.");
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(instanceId: string) {
    setBusy(true);
    try {
      await api.removeGroupMember(token(), id, instanceId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove the dial.");
    } finally {
      setBusy(false);
    }
  }

  async function reorder(index: number, dir: -1 | 1) {
    if (!group) return;
    const ids = group.members.map((m) => m.instanceId);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    setBusy(true);
    try {
      await api.reorderGroupMembers(token(), id, ids);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not reorder the dials.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete the group “${group?.name}”? The dials in it are not affected.`)) return;
    setBusy(true);
    try {
      await api.deleteGroup(token(), id);
      navigate("/instances");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the group.");
      setBusy(false);
    }
  }

  if (error) {
    return (
      <Centered>
        <span>{error}</span>
        <button onClick={() => navigate("/instances")} style={linkBtn}>Back to My Dials</button>
      </Centered>
    );
  }
  if (!group) {
    return <Centered>Loading…</Centered>;
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 28px 80px" }}>
      <button onClick={() => navigate("/instances")} className="kd-focusable" style={{ ...linkBtn, marginBottom: 16 }}>
        <Icon name="back" size={15} /> My Dials
      </button>

      <div className="kd-card" style={{ padding: "var(--pad-card)", display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Icon name="layers" size={22} style={{ color: "var(--text-muted)" }} />
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "var(--text)" }}>{group.name}</h1>
          </div>
          <div style={{ marginTop: 8 }}>
            <ScoreBadge score={group.score} classification={group.classification} />
          </div>
          <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
            {group.members.length} {group.members.length === 1 ? "dial" : "dials"}
            {group.hiddenMemberCount > 0 ? ` · ${group.hiddenMemberCount} hidden (no access)` : ""}
            {/* Partly-visible dials carry no band, so they are left out of the score above — say so rather
                than let the composite read as covering everything listed. */}
            {group.partialMemberCount
              ? ` · ${group.partialMemberCount} not scored (you can see part of ${group.partialMemberCount === 1 ? "it" : "them"})`
              : ""}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 12.5, color: "var(--text-muted)", fontWeight: 600, display: "inline-flex", gap: 6, alignItems: "center" }}>
            Rollup
            <select value={group.rollupStrategy} disabled={busy} onChange={(e) => changeStrategy(e.target.value)} style={ctl}>
              <option value="WorstOf">Worst of</option>
              <option value="Weighted">Weighted</option>
              <option value="Latest">Latest</option>
            </select>
          </label>
          <button onClick={() => setAdding(true)} disabled={busy} className="kd-focusable" style={ctl}><Icon name="plus" size={14} /> Add dials</button>
          <button onClick={rename} disabled={busy} className="kd-focusable" style={ctl}>Rename</button>
          <button onClick={remove} disabled={busy} className="kd-focusable" style={{ ...ctl, color: "var(--st-overdue)" }}>
            <Icon name="trash" size={14} /> Delete
          </button>
        </div>
      </div>

      <div style={{ display: "inline-flex", gap: 2, padding: 3, marginBottom: 18, borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface-2)" }}>
        <button onClick={() => setMode("portfolio")} className="kd-focusable" style={toggleBtn(mode === "portfolio")}>Portfolio</button>
        <button onClick={() => setMode("dial")} className="kd-focusable" style={toggleBtn(mode === "dial")}>Dial</button>
        <button onClick={() => setMode("byArea")} className="kd-focusable" style={toggleBtn(mode === "byArea")}>By area</button>
      </div>

      {group.members.map((m) => (
        <MemberRealtime key={m.instanceId} instanceId={m.instanceId} onChange={scheduleRefresh} />
      ))}

      {group.members.length === 0 ? (
        <div style={{ color: "var(--text-muted)", padding: 40, textAlign: "center" }}>No dials you can see are in this group.</div>
      ) : mode === "dial" ? (
        consolidated ? <GroupDial data={consolidated} /> : <div style={{ color: "var(--text-muted)", padding: 40, textAlign: "center" }}>Loading…</div>
      ) : mode === "portfolio" ? (
        <div className="kd-grid">
          {group.members.map((m, idx) => (
            <MemberCard
              key={m.instanceId}
              card={memberCards.get(m.instanceId) ?? null}
              name={m.name}
              org={m.organisationName}
              definitionName={m.definitionName}
              busy={busy}
              canMoveUp={idx > 0}
              canMoveDown={idx < group.members.length - 1}
              onOpen={() => navigate(`/instances/${m.instanceId}`, { state: { organisationId: m.organisationId, groupId: id, groupName: group.name } })}
              onMove={(dir) => reorder(idx, dir)}
              onRemove={() => removeMember(m.instanceId)}
            />
          ))}
        </div>
      ) : consolidated ? (
        <ConsolidatedMatrix data={consolidated} />
      ) : (
        <div style={{ color: "var(--text-muted)", padding: 40, textAlign: "center" }}>Loading…</div>
      )}

      {adding && (
        <AddMembersModal
          groupId={id}
          existing={new Set(group.members.map((m) => m.instanceId))}
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 28px", color: "var(--text-muted)", fontSize: 15, display: "flex", gap: 14, alignItems: "center", justifyContent: "center" }}>
      {children}
    </div>
  );
}

/** A zero-render component that keeps a realtime subscription to one member's instance scope, so a status
 * change in any member live-refreshes the group rollup. */
function MemberRealtime({ instanceId, onChange }: { instanceId: string; onChange: () => void }) {
  useRealtimeGroup(hubGroups.instance(instanceId), { onEvent: onChange, onReconnect: onChange });
  return null;
}

/** A group member shown as the same dial card as My Dials, with hover controls to reorder/remove it. */
/** A child's card data. `vm: null` with `needsSetup` is a child that has not been set up yet (KEY-434);
 * an absent entry is one whose detail simply did not load. */
interface MemberCardData {
  vm: DialVM | null;
  updated: string;
  needsSetup?: boolean;
}

function MemberCard({
  card,
  name,
  org,
  definitionName,
  busy,
  canMoveUp,
  canMoveDown,
  onOpen,
  onMove,
  onRemove,
}: {
  card: MemberCardData | null;
  name: string;
  org: string;
  definitionName: string;
  busy: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onOpen: () => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div style={{ position: "relative" }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <DialCard vm={card?.vm ?? null} name={name} org={org} definitionName={definitionName} updated={card?.updated ?? ""} needsSetup={card?.needsSetup ?? false} onOpen={onOpen} />
      {hover && (
        <div style={{ position: "absolute", top: 12, right: 12, display: "inline-flex", gap: 2, padding: 3, borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", boxShadow: "var(--shadow-sm)" }}>
          <CtlBtn title="Move up" disabled={busy || !canMoveUp} onClick={() => onMove(-1)}>
            <Icon name="chevDown" size={14} style={{ transform: "rotate(180deg)" }} />
          </CtlBtn>
          <CtlBtn title="Move down" disabled={busy || !canMoveDown} onClick={() => onMove(1)}>
            <Icon name="chevDown" size={14} />
          </CtlBtn>
          <CtlBtn title="Remove from group" disabled={busy} onClick={onRemove}>
            <Icon name="x" size={14} />
          </CtlBtn>
        </div>
      )}
    </div>
  );
}

function CtlBtn({ title, disabled, onClick, children }: { title: string; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={disabled}
      title={title}
      aria-label={title}
      className="kd-focusable"
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, border: "none", borderRadius: "var(--radius-sm)", background: "transparent", color: "var(--text-muted)", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.4 : 1 }}
    >
      {children}
    </button>
  );
}

function toggleBtn(active: boolean): CSSProperties {
  return {
    padding: "6px 14px",
    borderRadius: "var(--radius-sm)",
    border: "none",
    background: active ? "var(--surface)" : "transparent",
    color: active ? "var(--text)" : "var(--text-muted)",
    font: "inherit",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  };
}

const ctl: CSSProperties = { padding: "7px 11px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)", font: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };
const linkBtn: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: "var(--radius-sm)", border: "none", background: "none", color: "var(--text-muted)", font: "inherit", fontWeight: 600, fontSize: 13.5, cursor: "pointer" };
